import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Columns3, Copy, Eye, EyeOff, RefreshCw, Search, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import adminOpsService from '../../../services/adminOpsService';

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];
export const COLUMN_STORAGE_KEY = 'fundelite.admin.911proxyIpUsage.columns.v1';
const SESSION_PAGE_SIZE = 25;

export const PROVIDER_LABELS = {
  '911proxy': '911Proxy',
  dataimpulse: 'DataImpulse',
};

export const IP_USAGE_COLUMNS = [
  { id: 'exit_ip', label: 'Exit IP', locked: true, defaultVisible: true },
  { id: 'country', label: 'Country', defaultVisible: false },
  { id: 'requested_state', label: 'Requested State', defaultVisible: false },
  { id: 'state', label: 'State', selectorLabel: 'Observed State', defaultVisible: true },
  { id: 'city', label: 'City', selectorLabel: 'Observed City', defaultVisible: true },
  { id: 'postal', label: 'Postal', selectorLabel: 'Observed Postal', defaultVisible: false },
  { id: 'client', label: 'Client', defaultVisible: true },
  { id: 'profile', label: 'Profile', selectorLabel: 'Profile ID', defaultVisible: true },
  { id: 'assignment', label: 'Assignment', selectorLabel: 'Assignment ID', defaultVisible: false },
  { id: 'first_seen', label: 'First Used', defaultVisible: true },
  { id: 'last_seen', label: 'Last Used', defaultVisible: true },
  { id: 'sessions', label: 'Sessions', selectorLabel: 'Session Count', defaultVisible: true },
  { id: 'unique_clients', label: 'Unique Clients', defaultVisible: false },
  { id: 'reuse', label: 'Reuse', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true },
  { id: 'health', label: 'Health', defaultVisible: false },
  { id: 'actions', label: 'Actions', locked: true, defaultVisible: true },
];

const REUSE_LABELS = {
  new: 'NEW IP',
  same_client_reuse: 'SAME CLIENT',
  different_client_reuse: 'DIFFERENT CLIENT',
};
const REUSE_TONES = {
  new: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  same_client_reuse: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  different_client_reuse: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
};
const STATUS_TONES = {
  active: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  stale: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  closed: 'bg-stone-700/40 text-slate-300 border-stone-600',
  expired: 'bg-red-500/10 text-red-300 border-red-500/30',
};
const HEALTH_TONES = {
  healthy: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  degraded: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  expired: 'bg-red-500/10 text-red-300 border-red-500/30',
};

const inputClass =
  'bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600';
const buttonClass =
  'inline-flex items-center gap-2 rounded-lg border border-stone-700 bg-stone-900 px-3 py-2 text-sm text-slate-200 hover:bg-stone-800 disabled:opacity-50';

export function defaultColumnVisibility() {
  return Object.fromEntries(IP_USAGE_COLUMNS.map((col) => [col.id, col.defaultVisible]));
}

export function loadColumnVisibility() {
  const defaults = defaultColumnVisibility();
  try {
    const stored = JSON.parse(window.localStorage.getItem(COLUMN_STORAGE_KEY) || 'null');
    if (!stored || typeof stored !== 'object') return defaults;
    const merged = { ...defaults };
    IP_USAGE_COLUMNS.forEach((col) => {
      if (!col.locked && typeof stored[col.id] === 'boolean') merged[col.id] = stored[col.id];
    });
    return merged;
  } catch {
    return defaults;
  }
}

function saveColumnVisibility(visibility) {
  try {
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibility));
  } catch {
    /* storage unavailable: preference stays in memory */
  }
}

const GEO_SOURCE_LABELS = {
  launcher_exit_lookup: 'QA Browser exit lookup (ipapi.co / ipinfo.io)',
};

export function formatGeoSource(value) {
  if (!value) return 'Not recorded';
  return GEO_SOURCE_LABELS[value] || value;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.round(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function safeMessage(error, fallback) {
  const message = String(error?.message || '').trim();
  if (!message || /traceback|secret|password|app_key/i.test(message)) return fallback;
  return message;
}

function Badge({ tone, children, testId }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide ${tone}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

export function ReuseBadge({ value }) {
  if (!value) return <span className="text-slate-500">—</span>;
  return (
    <Badge tone={REUSE_TONES[value] || STATUS_TONES.closed} testId={`reuse-badge-${value}`}>
      {REUSE_LABELS[value] || value}
    </Badge>
  );
}

export function StatusBadge({ value }) {
  if (!value) return <span className="text-slate-500">—</span>;
  return (
    <Badge tone={STATUS_TONES[value] || STATUS_TONES.closed} testId={`status-badge-${value}`}>
      {value.toUpperCase()}
    </Badge>
  );
}

export function HealthBadge({ value }) {
  if (!value) return <span className="text-slate-500">—</span>;
  return (
    <Badge tone={HEALTH_TONES[value] || STATUS_TONES.closed} testId={`health-badge-${value}`}>
      {value.toUpperCase()}
    </Badge>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
      <p className="text-sm text-slate-500 mb-1">{label}</p>
      <p className="text-3xl font-bold text-white" data-testid={testId}>{value}</p>
    </div>
  );
}

function IpUsageSummary({ summary }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ip-usage-summary">
      <StatCard label="Unique Exit IPs" value={summary?.unique_exit_ips ?? 0} testId="ip-usage-stat-unique" />
      <StatCard label="Active Sessions" value={summary?.active_sessions ?? 0} testId="ip-usage-stat-active" />
      <StatCard label="Reused IPs" value={summary?.reused_ips ?? 0} testId="ip-usage-stat-reused" />
      <StatCard
        label="Different-Client Reuse"
        value={summary?.different_client_reuse ?? 0}
        testId="ip-usage-stat-different-client"
      />
    </div>
  );
}

function ColumnSelector({ visibility, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className={buttonClass}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        data-testid="ip-usage-columns-toggle"
      >
        <Columns3 size={16} />
        Columns
      </button>
      {open ? (
        <div
          className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-stone-800 bg-stone-950 p-2 shadow-xl"
          data-testid="ip-usage-columns-menu"
        >
          {IP_USAGE_COLUMNS.filter((col) => !col.locked).map((col) => (
            <label key={col.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-300 hover:bg-stone-900">
              <input
                type="checkbox"
                checked={Boolean(visibility[col.id])}
                onChange={() => onToggle(col.id)}
                data-testid={`ip-usage-column-${col.id}`}
              />
              {col.selectorLabel || col.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function IpCell({ row, revealedIp, onReveal, onHide, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-slate-100" data-testid={`ip-usage-ip-${row.id}`}>
        {revealedIp || row.exit_ip_masked || 'hidden'}
      </span>
      <button
        type="button"
        className="text-slate-500 hover:text-slate-200"
        title={revealedIp ? 'Hide IP' : 'Reveal IP'}
        aria-label={revealedIp ? 'Hide IP' : 'Reveal IP'}
        onClick={(event) => {
          event.stopPropagation();
          if (revealedIp) onHide(row);
          else onReveal(row);
        }}
        data-testid={`ip-usage-reveal-${row.id}`}
      >
        {revealedIp ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
      <button
        type="button"
        className="text-slate-500 hover:text-slate-200"
        title="Copy IP"
        aria-label="Copy IP"
        onClick={(event) => {
          event.stopPropagation();
          onCopy(row);
        }}
        data-testid={`ip-usage-copy-${row.id}`}
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

function cellFor(columnId, row, ipProps) {
  switch (columnId) {
    case 'exit_ip':
      return <IpCell row={row} {...ipProps} />;
    case 'country':
      return row.country || '—';
    case 'requested_state':
      return row.requested_state || '—';
    case 'state':
      return row.state || '—';
    case 'city':
      return row.city || '—';
    case 'postal':
      return row.postal || '—';
    case 'client':
      return row.last_client_display_name || row.last_client_id || '—';
    case 'profile':
      return <span className="font-mono text-xs">{row.profile_id || '—'}</span>;
    case 'assignment':
      return <span className="font-mono text-xs">{row.assignment_id || '—'}</span>;
    case 'first_seen':
      return formatDateTime(row.first_seen_at);
    case 'last_seen':
      return formatDateTime(row.last_seen_at);
    case 'sessions':
      return row.session_count ?? 0;
    case 'unique_clients':
      return row.unique_client_count ?? 0;
    case 'reuse':
      return <ReuseBadge value={row.reuse_classification} />;
    case 'status':
      return <StatusBadge value={row.status} />;
    case 'health':
      return <HealthBadge value={row.health} />;
    default:
      return null;
  }
}

function IpUsageTable({ items, loading, columns, hasFilters, onSelect, ipPropsFor, providerLabel }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-stone-800 bg-stone-900">
      <table className="w-full text-sm" data-testid="ip-usage-table">
        <thead className="bg-stone-950/60">
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !items.length ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-slate-500" data-testid="ip-usage-loading">
                Loading IP usage…
              </td>
            </tr>
          ) : null}
          {!loading && !items.length ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center" data-testid="ip-usage-empty">
                {hasFilters ? (
                  <p className="text-slate-400">No IPs match these filters.</p>
                ) : (
                  <>
                    <p className="text-base font-semibold text-white">No {providerLabel} IP usage yet</p>
                    <p className="mt-1 text-slate-500">
                      Residential exit IPs will appear here after a verified {providerLabel} QA session is launched.
                    </p>
                  </>
                )}
              </td>
            </tr>
          ) : null}
          {items.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-t border-stone-800 text-slate-300 hover:bg-stone-800/50"
              onClick={() => onSelect(row)}
              data-testid={`ip-usage-row-${row.id}`}
            >
              {columns.map((col) => (
                <td key={col.id} className="whitespace-nowrap px-4 py-3">
                  {col.id === 'actions' ? (
                    <button
                      type="button"
                      className="rounded-md border border-stone-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-stone-800"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(row);
                      }}
                      data-testid={`ip-usage-view-${row.id}`}
                    >
                      View
                    </button>
                  ) : (
                    cellFor(col.id, row, ipPropsFor(row))
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IpUsagePagination({ page, pageSize, total, totalPages, onPageChange, onPageSizeChange }) {
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-col gap-3 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between" data-testid="ip-usage-pagination">
      <span data-testid="ip-usage-range">
        Showing {from}–{to} of {total} IPs
      </span>
      <div className="flex flex-wrap items-center gap-3">
        <select
          className={inputClass}
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          aria-label="Rows per page"
          data-testid="ip-usage-page-size"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{`${size} / page`}</option>
          ))}
        </select>
        <button
          type="button"
          className={buttonClass}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid="ip-usage-prev"
        >
          Previous
        </button>
        <span data-testid="ip-usage-page-label">Page {page} of {totalPages}</span>
        <button
          type="button"
          className={buttonClass}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="ip-usage-next"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200">{children}</p>
    </div>
  );
}

function IpUsageDrawer({ usage, revealedIp, onReveal, onHide, onCopy, onClose, provider, providerLabel }) {
  const { getAuthHeaders } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [sessionPage, setSessionPage] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    (async () => {
      try {
        adminOpsService.setAuthHeaders(getAuthHeaders());
        const result = await adminOpsService.listProviderIpSessions(provider, usage.id, {
          page: sessionPage,
          pageSize: SESSION_PAGE_SIZE,
        });
        if (!cancelled) setSessions(result);
      } catch (err) {
        if (!cancelled) setError(safeMessage(err, 'Unable to load session history.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [usage.id, sessionPage, provider]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const totalPages = sessions?.total_pages || 1;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ip-usage-drawer-root">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        data-testid="ip-usage-drawer-backdrop"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="ip-usage-drawer-title"
        className="relative flex h-full w-full flex-col overflow-y-auto border-stone-800 bg-stone-950 md:max-w-2xl md:border-l"
        data-testid="ip-usage-drawer"
      >
        <div className="flex items-start justify-between gap-4 border-b border-stone-800 p-5">
          <div>
            <p id="ip-usage-drawer-title" className="text-sm text-slate-500">{providerLabel} Exit IP</p>
            <div className="mt-1 flex items-center gap-3">
              <span className="font-mono text-2xl font-bold text-white" data-testid="ip-usage-drawer-ip">
                {revealedIp || usage.exit_ip_masked}
              </span>
              <button
                type="button"
                className={buttonClass}
                onClick={() => (revealedIp ? onHide(usage) : onReveal(usage))}
                data-testid="ip-usage-drawer-reveal"
              >
                {revealedIp ? <EyeOff size={14} /> : <Eye size={14} />}
                {revealedIp ? 'Hide' : 'Reveal'}
              </button>
              <button type="button" className={buttonClass} onClick={() => onCopy(usage)} data-testid="ip-usage-drawer-copy">
                <Copy size={14} />
                Copy
              </button>
            </div>
          </div>
          <button
            type="button"
            className="text-slate-400 hover:text-white"
            onClick={onClose}
            aria-label="Close details"
            data-testid="ip-usage-drawer-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 border-b border-stone-800 p-5 sm:grid-cols-3" data-testid="ip-usage-drawer-summary">
          <DetailField label="Provider">{providerLabel}</DetailField>
          <DetailField label="Country">{usage.country || '—'}</DetailField>
          <DetailField label="State">{usage.state || '—'}</DetailField>
          <DetailField label="City">{usage.city || '—'}</DetailField>
          <DetailField label="Geo Source">
            <span data-testid="ip-usage-drawer-geo-source">{formatGeoSource(usage.geo_source)}</span>
          </DetailField>
          <DetailField label="First Seen">{formatDateTime(usage.first_seen_at)}</DetailField>
          <DetailField label="Last Seen">{formatDateTime(usage.last_seen_at)}</DetailField>
          <DetailField label="Total Sessions">{usage.session_count ?? 0}</DetailField>
          <DetailField label="Unique Clients">{usage.unique_client_count ?? 0}</DetailField>
          <DetailField label="Reuse"><ReuseBadge value={usage.reuse_classification} /></DetailField>
        </div>

        <div className="space-y-3 p-5">
          <h3 className="text-base font-semibold text-white">Session History</h3>
          {error ? <p className="text-sm text-red-300">{error}</p> : null}
          {!sessions && !error ? <p className="text-sm text-slate-500" data-testid="ip-usage-sessions-loading">Loading sessions…</p> : null}
          {sessions ? (
            <div className="overflow-x-auto rounded-xl border border-stone-800">
              <table className="w-full text-sm" data-testid="ip-usage-sessions-table">
                <thead className="bg-stone-900">
                  <tr>
                    {['Date', 'Client', 'Profile', 'Requested State', 'Observed State', 'Observed City', 'Duration', 'Health', 'Status'].map(
                      (label) => (
                        <th key={label} className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">
                          {label}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sessions.items.length ? (
                    sessions.items.map((session) => (
                      <tr key={session.id} className="border-t border-stone-800 text-slate-300" data-testid={`ip-usage-session-${session.id}`}>
                        <td className="whitespace-nowrap px-3 py-2">{formatDateTime(session.first_seen_at)}</td>
                        <td className="whitespace-nowrap px-3 py-2">{session.client_display_name || session.client_id || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{session.profile_id || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">{session.requested_state || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">{session.observed_state || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">{session.observed_city || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">{formatDuration(session.duration_seconds)}</td>
                        <td className="px-3 py-2"><HealthBadge value={session.health} /></td>
                        <td className="px-3 py-2"><StatusBadge value={session.status} /></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                        Detailed sessions for this IP are past the retention window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
          {sessions && totalPages > 1 ? (
            <div className="flex items-center justify-end gap-3 text-sm text-slate-400">
              <button type="button" className={buttonClass} disabled={sessionPage <= 1} onClick={() => setSessionPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {sessionPage} of {totalPages}</span>
              <button
                type="button"
                className={buttonClass}
                disabled={sessionPage >= totalPages}
                onClick={() => setSessionPage((p) => p + 1)}
                data-testid="ip-usage-sessions-next"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

const EMPTY_FILTERS = {
  search: '',
  state: '',
  city: '',
  reuse: '',
  status: '',
  health: '',
  dateFrom: '',
  dateTo: '',
};

function dateBoundary(value, endOfDay) {
  if (!value) return undefined;
  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export default function Proxy911IpUsagePanel({ provider = '911proxy' }) {
  const providerLabel = PROVIDER_LABELS[provider] || provider;
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [visibility, setVisibility] = useState(loadColumnVisibility);
  const requestId = useRef(0);

  useEffect(() => {
    const current = ++requestId.current;
    setLoading(true);
    setError('');
    (async () => {
      try {
        adminOpsService.setAuthHeaders(getAuthHeaders());
        const [usage, summaryResult] = await Promise.all([
          adminOpsService.listProviderIpUsage(provider, {
            page,
            page_size: pageSize,
            sort: 'last_seen_at',
            direction: 'desc',
            search: filters.search.trim() || undefined,
            state: filters.state.trim() || undefined,
            city: filters.city.trim() || undefined,
            reuse_classification: filters.reuse || undefined,
            status: filters.status || undefined,
            health: filters.health || undefined,
            date_from: dateBoundary(filters.dateFrom, false),
            date_to: dateBoundary(filters.dateTo, true),
          }),
          adminOpsService.getProviderIpUsageSummary(provider),
        ]);
        if (current !== requestId.current) return;
        setItems(usage?.items || []);
        setTotal(usage?.total || 0);
        setTotalPages(usage?.total_pages || 1);
        setSummary(summaryResult || null);
      } catch (err) {
        if (current !== requestId.current) return;
        setItems([]);
        setTotal(0);
        setTotalPages(1);
        setError(safeMessage(err, `Unable to load ${providerLabel} IP usage.`));
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    })();
  }, [page, pageSize, filters, refreshKey, provider, providerLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDraft = (field) => (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));

  const applyNow = (field) => (event) => {
    const value = event.target.value;
    setDraft((current) => ({ ...current, [field]: value }));
    setFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  };

  const applySearch = (event) => {
    event.preventDefault();
    setFilters(draft);
    setPage(1);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const toggleColumn = (id) => {
    setVisibility((current) => {
      const next = { ...current, [id]: !current[id] };
      saveColumnVisibility(next);
      return next;
    });
  };

  const reveal = useCallback(async (row) => {
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const result = await adminOpsService.revealProviderIp(provider, row.id);
      setRevealed((current) => ({ ...current, [row.id]: result?.exit_ip || '' }));
    } catch (err) {
      toast.error(safeMessage(err, 'Unable to reveal this IP.'));
    }
  }, [getAuthHeaders, provider]);

  const hide = useCallback((row) => {
    setRevealed((current) => {
      const next = { ...current };
      delete next[row.id];
      return next;
    });
  }, []);

  const copy = useCallback(async (row) => {
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const result = await adminOpsService.copyProviderIp(provider, row.id);
      if (!result?.exit_ip || !navigator.clipboard?.writeText) throw new Error('Clipboard is not available.');
      await navigator.clipboard.writeText(result.exit_ip);
      toast.success('IP copied');
    } catch (err) {
      toast.error(safeMessage(err, 'Unable to copy this IP.'));
    }
  }, [getAuthHeaders, provider]);

  const closeDrawer = useCallback(() => setSelected(null), []);
  const ipPropsFor = (row) => ({ revealedIp: revealed[row.id], onReveal: reveal, onHide: hide, onCopy: copy });
  const columns = IP_USAGE_COLUMNS.filter((col) => col.locked || visibility[col.id]);
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-5" data-testid="proxy911-ip-usage-panel">
      <IpUsageSummary summary={summary} />

      <form
        className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-800 bg-stone-900 p-4"
        onSubmit={applySearch}
        data-testid="ip-usage-toolbar"
      >
        <label className="relative min-w-[220px] flex-1">
          <span className="sr-only">Search</span>
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className={`${inputClass} w-full pl-9`}
            value={draft.search}
            onChange={updateDraft('search')}
            placeholder="Search IP, client, profile..."
            data-testid="ip-usage-search"
          />
        </label>
        <input
          className={`${inputClass} w-32`}
          value={draft.state}
          onChange={updateDraft('state')}
          placeholder="State"
          aria-label="State"
          data-testid="ip-usage-filter-state"
        />
        <input
          className={`${inputClass} w-32`}
          value={draft.city}
          onChange={updateDraft('city')}
          placeholder="City"
          aria-label="City"
          data-testid="ip-usage-filter-city"
        />
        <select className={inputClass} value={draft.reuse} onChange={applyNow('reuse')} aria-label="Reuse" data-testid="ip-usage-filter-reuse">
          <option value="">All reuse</option>
          <option value="new">New IP</option>
          <option value="same_client_reuse">Same client</option>
          <option value="different_client_reuse">Different client</option>
        </select>
        <select className={inputClass} value={draft.status} onChange={applyNow('status')} aria-label="Status" data-testid="ip-usage-filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="stale">Stale</option>
          <option value="closed">Closed</option>
          <option value="expired">Expired</option>
        </select>
        <select className={inputClass} value={draft.health} onChange={applyNow('health')} aria-label="Health" data-testid="ip-usage-filter-health">
          <option value="">All health</option>
          <option value="healthy">Healthy</option>
          <option value="degraded">Degraded</option>
          <option value="expired">Expired</option>
        </select>
        <input
          type="date"
          className={inputClass}
          value={draft.dateFrom}
          onChange={applyNow('dateFrom')}
          aria-label="Last used from"
          data-testid="ip-usage-filter-date-from"
        />
        <input
          type="date"
          className={inputClass}
          value={draft.dateTo}
          onChange={applyNow('dateTo')}
          aria-label="Last used to"
          data-testid="ip-usage-filter-date-to"
        />
        <button type="submit" className={buttonClass} data-testid="ip-usage-search-submit">
          <Search size={16} />
          Search
        </button>
        {hasFilters ? (
          <button type="button" className={buttonClass} onClick={clearFilters} data-testid="ip-usage-clear">
            Clear
          </button>
        ) : null}
        <ColumnSelector visibility={visibility} onToggle={toggleColumn} />
        <button
          type="button"
          className={buttonClass}
          onClick={() => setRefreshKey((key) => key + 1)}
          disabled={loading}
          aria-label="Refresh"
          data-testid="ip-usage-refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </form>

      {error ? (
        <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4" data-testid="ip-usage-error">
          <AlertTriangle className="mt-0.5 flex-shrink-0 text-red-400" size={20} />
          <p className="text-sm text-red-200">{error}</p>
        </div>
      ) : null}

      <IpUsageTable
        items={items}
        loading={loading}
        columns={columns}
        hasFilters={hasFilters}
        onSelect={setSelected}
        ipPropsFor={ipPropsFor}
        providerLabel={providerLabel}
      />

      <IpUsagePagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={(next) => setPage(Math.max(1, Math.min(totalPages, next)))}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
      />

      {selected ? (
        <IpUsageDrawer
          usage={selected}
          revealedIp={revealed[selected.id]}
          onReveal={reveal}
          onHide={hide}
          onCopy={copy}
          onClose={closeDrawer}
          provider={provider}
          providerLabel={providerLabel}
        />
      ) : null}
    </div>
  );
}
